<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import HelpSection from "../components/HelpSection.svelte";
	import Contact from "../components/Contact.svelte";
	import type { ReferenceDetailProps } from "./ReferenceDetailProps.js";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import Icon from "../components/Icon.svelte";
	import arrowDown from "../../../core/assets/icons/filled-arrow-down.svg?raw";
	import DynamicPicture from "../components/Picture/DynamicPicture.svelte";
	import arrowLeft from "../../../core/assets/icons/filled-arrow-left.svg?raw";
	import SimilarReferences from "../components/References/SimilarReferences.svelte";
	import Content from "../components/Content.svelte";
	import PhotoGalleryCarousel from "../components/PhotoGalleryCarousel.svelte";
	import { contactPathMap } from "../../../core/schema/paths/contactPathMap";
	import Link from "../components/Link.svelte";
	import { nbspify } from "../../../core/utils/nbspify";
	import { setContext } from "svelte";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import Products from "../components/Products.svelte";
	import { referencesPathMap } from "../../../core/schema/paths/referencesPathMap";
	import { caseStudyCategoryUrlMap } from "../../../core/schema/names/caseStudyCategoryUrlMap";
	import { referencesAnchorPathMap } from "../../../core/schema/paths/referencesAnchorPathMap";

	type $$Props = ReferenceDetailProps;

	const { relatedProducts, contact, reference, similarReferences, categories } = $$restProps as $$Props;

	const { TENANT_LOCALE, STORAGE_URL } = getGlobalSettings();
	setContext(thumbnailImageContextKey, `${STORAGE_URL}/${reference.thumbnailImage.path}`);

	/* eslint-disable prefer-named-capture-group */
</script>

<Layout>
	<button
		class="ml-6 mt-4 flex cursor-pointer gap-4 md:ml-10 md:mt-8"
		on:click={() => {
			const references = referencesPathMap[TENANT_LOCALE];
			const category = caseStudyCategoryUrlMap[reference.caseStudyCategory][TENANT_LOCALE];
			const anchor = referencesAnchorPathMap[TENANT_LOCALE];
			window.location.href = `/${references}/${category}${anchor}`;
		}}
	>
		<Icon icon={arrowLeft} class="text-gray h-4 w-4" />
		<span class="text-xs font-extrabold">Zpět</span>
	</button>
	<div class="mx-auto mt-7 flex flex-col justify-center px-4">
		<span
			class="font-quicksand text-primary m-0 text-center text-[1.375rem] font-bold uppercase leading-10 tracking-[0.3em] md:text-[2rem]"
		>
			Reference
		</span>
		<h1
			class="font-fustat mx-auto my-6 max-w-[70.625rem] pt-0 text-center text-lg font-bold leading-[1.25] text-[#8F8F8F] sm:text-[2rem] md:text-[3rem] lg:mb-8 lg:mt-16"
		>
			{nbspify(reference.title[TENANT_LOCALE])}
		</h1>
		<div class="text-md text-bito-black mx-auto max-w-[70.625rem] text-center font-extrabold md:text-[1.5rem]">
			<Content content={reference.perex[TENANT_LOCALE]} contentClass={false} />
		</div>
		<a
			aria-label="Přeskočit na obsah"
			href="#obsah"
			class="border-gray text-secondary hover:border-tertiary hover:text-tertiary group mx-auto mt-16 flex h-[4.5rem] w-[4.5rem] items-center justify-center border"
		>
			<Icon icon={arrowDown} class="h-4 w-4 transition-transform duration-200 ease-out group-hover:scale-[130%]" />
		</a>
	</div>

	<PhotoGalleryCarousel photoGallery={reference.photoGallery} thumbnailImage={reference.thumbnailImage} />

	<div
		id="obsah"
		class="mx-auto mt-20 flex w-fit flex-col items-center gap-16 px-4 md:px-8 lg:w-full lg:max-w-[70.688rem] lg:flex-row lg:items-start lg:gap-[8.438rem] xl:px-0"
	>
		{#if contact !== undefined}
			<div class="flex flex-col self-start">
				<DynamicPicture
					alt={contact.fullName}
					bucket="storage"
					height={240}
					image={contact.photoForCaseStudies}
					imgClass="w-2/3 h-auto object-contain"
					width={166}
				/>
				<div class="border-gray flex w-full max-w-[20.313rem] flex-col gap-2 border px-6 pb-6 pt-5">
					<p class="text-[1.5rem] font-extrabold">{nbspify(contact.fullName)}</p>
					<p class="text-xs">{nbspify(contact.position[TENANT_LOCALE])}</p>
					<div class="flex flex-col gap-4">
						<p class="text-primary font-extrabold">
							<a href="tel:00420{contact.phoneNumber.replaceAll(' ', '')}">
								{nbspify(`+420 ${contact.phoneNumber.replace(/(\d{3})\s*(\d{3})\s*(\d{3})/u, "$1 $2 $3")}`)}
							</a>
							<br />
							<a href="mailto:{contact.email}">
								{nbspify(contact.email)}
							</a>
						</p>
						<p class="whitespace-pre-line text-xs text-gray-400">
							{nbspify(reference.shortDescription[TENANT_LOCALE])}
						</p>
					</div>
				</div>
				<Link asButton variant="primary" href="/{contactPathMap[TENANT_LOCALE]}" class="w-[7.5rem] sm:w-[13.75rem]">
					Kontaktovat
				</Link>
			</div>
		{/if}

		<div class="flex w-full flex-col gap-14">
			{#if reference.clientLogo}
				<DynamicPicture
					image={reference.clientLogo}
					alt="Logo klienta"
					bucket="storage"
					width={400}
					height={300}
					imgClass="max-h-[12.5rem] w-auto"
				/>
			{/if}
			<Content content={reference.content[TENANT_LOCALE]} contentClass="ckeditor-content ckeditor-bito-text" />
		</div>
	</div>
	<div class="container mt-20 flex flex-col gap-20 lg:mt-32 lg:gap-32">
		<Contact />
		{#if similarReferences.length > 0}
			<SimilarReferences {similarReferences} title="Podobné reference" />
		{/if}
		{#if relatedProducts.length > 0}
			<Products title="Související produkty" products={relatedProducts} {categories} />
		{/if}
		<HelpSection />
	</div>
</Layout>
